import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Plus } from 'lucide-react';
import Layout from '@/components/Layout';
import { ADMIN_CPM_CAMPAIGNS } from '@/queries/queries';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatCents } from '@/lib/format';
import { CampaignCpmRateList } from '@/components/campaigns/CampaignCpmRateList';
import { describeCampaignCpmRates } from '@/lib/campaigns/describeCampaignCpmRates';
import { RankingCampaignSummary } from '@/components/campaigns/RankingCampaignSummary';
import CampaignBudgetBar from '@/components/CampaignBudgetBar';

export default function AdminCampaignsListPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string | undefined>(undefined);
  const { data, loading } = useQuery(ADMIN_CPM_CAMPAIGNS, {
    variables: { status, limit: 100, offset: 0 },
  });

  const campaigns = (data?.adminCpmCampaigns?.data || []) as any[];
  const total = data?.adminCpmCampaigns?.total ?? 0;

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">
                  {t('admin.campaigns.title')}
                </h1>
                <p className="text-muted-foreground">
                  {t('admin.campaigns.subtitle')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-red-500 border-red-500">ADMIN</Badge>
              <Button asChild>
                <Link to="/admin/campaigns/new">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('admin.campaigns.newCampaign')}
                </Link>
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('admin.campaigns.allCampaigns')}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t('admin.common.total')}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-4">
                <Select
                  value={status || 'all'}
                  onValueChange={(v) => setStatus(v === 'all' ? undefined : v)}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.campaigns.allStatuses')}</SelectItem>
                    <SelectItem value="draft">{t('admin.campaigns.status.draft')}</SelectItem>
                    <SelectItem value="active">{t('admin.campaigns.status.active')}</SelectItem>
                    <SelectItem value="paused">{t('admin.campaigns.status.paused')}</SelectItem>
                    <SelectItem value="ended">{t('admin.campaigns.status.ended')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {loading ? (
                <p className="text-sm text-muted-foreground">
                  {t('admin.campaigns.loading')}
                </p>
              ) : campaigns.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('admin.campaigns.empty')}
                </p>
              ) : (
                <div className="grid gap-3">
                  {campaigns.map((c) => (
                    <Link key={c.id} to={`/admin/campaigns/${c.id}`} className="block">
                      <Card className="p-4 hover:shadow-md transition">
                        <div className="flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold truncate">{c.name}</h3>
                              <Badge variant="outline">
                                {t(`admin.campaigns.status.${c.status}`)}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t('admin.campaigns.submissionsCount', {
                                count: c.submissions_count,
                              })}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0 min-w-[12rem]">
                            <p className="text-sm font-medium">
                              {formatCents(c.budget_spent_cents, c.currency)}
                              {' / '}
                              {formatCents(c.budget_total_cents, c.currency)}
                            </p>
                            <CampaignBudgetBar
                              totalCents={c.budget_total_cents}
                              spentCents={Math.max(
                                0,
                                (c.budget_total_cents || 0) - (c.budget_remaining_cents || 0),
                              )}
                              pendingCents={c.budget_pending_cents}
                              currency={c.currency}
                              size="sm"
                              className="mt-1.5"
                            />
                            {Number.isInteger(c.budget_spend_drift_cents)
                              && c.budget_spend_drift_cents !== 0 && (
                              <p className="text-[11px] text-amber-400 mt-1">
                                {c.budget_spend_drift_cents > 0
                                  ? t('campaign.budgetSpendDrift', {
                                    stored: formatCents(c.budget_stored_spent_cents || 0, c.currency),
                                    approved: formatCents(c.budget_spent_cents || 0, c.currency),
                                    amount: formatCents(c.budget_spend_drift_cents, c.currency),
                                  })
                                  : t('campaign.budgetSpendDriftBehind', {
                                    stored: formatCents(c.budget_stored_spent_cents || 0, c.currency),
                                    approved: formatCents(c.budget_spent_cents || 0, c.currency),
                                  })}
                              </p>
                            )}
                            {c.campaign_type === 'ranking' ? <RankingCampaignSummary config={c.ranking_config} currency={c.currency} /> : (() => {
                              const rates = describeCampaignCpmRates(c);
                              return rates.kind === 'single' ? (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatCents(rates.rateCents, c.currency)}
                                  {' '}
                                  {t('admin.campaigns.perThousandViews')}
                                </p>
                              ) : (
                                <CampaignCpmRateList rates={rates.rates} currency={c.currency} className="mt-1" />
                              );
                            })()}
                          </div>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </Layout>
  );
}
